import React, {Component} from 'react'
import "./InsuranceAppointment.css"
import {DateNavigation} from "../../../../../components/datenavigation/DateNavigation";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import PageTitle from "../../../../../layout/PageTitle";
import DateUtil from "../../../../../utils/DateUtil";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import YesNoNotAvailableEnum from "../../../../genericenums/YesNoNotAvailableEnum";
import EligibilityStatusEnum from "../../../enums/EligibilityStatusEnum";
import moment from "moment";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Dropdown as PrimeDropdown} from "primereact/components/dropdown/Dropdown";
import {noop, pick} from "lodash-es";
import InsuranceDocumentLegend from "../../InsuranceDocumentLegend";
import EnumDirectBillingType from "../../direct/EnumDirectBillingType";
import appointment from "../../../../appointment/Appointment";
import AuthService from "../../../../../services/auth-service/AuthService";
import dialog from "../../../../../components/dialog/Dialog";
import EntityNoteListModal from "../notes/EntityNoteListModal";
import HoverableDescriptionBox from "../../../../../components/hoverable-desc-box/HoverableDescriptionBox";


export default class InsuranceAppointment extends Component {
    constructor(props) {
        super(props);

        this.canEditApprovals = AuthService.userHasAuthority("UPDATE_INSURANCE_APPOINTMENTS");
        // ADD_APPOINTMENT_NOTE lets note-only users (e.g. CALL_CENTER) manage notes
        // without the full UPDATE_INSURANCE_APPOINTMENTS capability.
        this.canAddNotes = AuthService.userHasAnyAuthority(["UPDATE_INSURANCE_APPOINTMENTS", "ADD_APPOINTMENT_NOTE"]);

        this.state = {
            items: [],
            selectedItem: null,
            errorMessages: [],
            selectedDate: new Date()
        };
    }

    componentDidMount() {
        if (this.props.selected) {
            this.retrieveInsuranceApprovals();
        }
    }

    componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS) {
        if (!prevProps.selected && this.props.selected) {
            this.retrieveInsuranceApprovals();
        }
    }

    handleDateChange = (date) => {
        this.setState({selectedDate: date}, () => {
            if (date) {
                this.retrieveInsuranceApprovals();
            }
        });
    }

    retrieveInsuranceApprovals() {
        const date = this.state.selectedDate;
        if (!date) {
            return;
        }
        RemotingService.getRemoteCall('api/insurance/coordinator/v2/insurance-approval',
            {
                date: moment(date).format('YYYY-MM-DD'),
            },
            (result) => {
                this.setState({items: result.filter(item =>
                        item.directBilling.usedAppointmentIds.includes(item.appointmentId)
                    )});
            });
    }

    openNotesModal = (item) => {
        dialog({
            title: "Appointment Notes",
            component: <EntityNoteListModal entityId={item.id}
                                            baseUrl={"api/insurance/coordinator/v2/insurance-approval"}
                                            readOnly={!this.canAddNotes}
                                            onNotesChange={() => this.retrieveInsuranceApprovals()}/>,
            size: "lg"
        });
    }

    notesTemplate = (item) => {
        const notes = item.notes || [];
        const notesCount = notes.filter(note => note.isActive !== false).length;
        return <Button variant={"link"} style={{color: "#007bff", fontSize: "14px"}}
                       onClick={() => this.openNotesModal(item)}>
            <FontAwesomeIcon icon={["fas", "sticky-note"]} size={"sm"}/> Notes ({notesCount})
        </Button>;
    }

    dateTimeTemplate(item) {
        return DateUtil.formatClinicDateTime(item.visitTime, "Do MMMM YYYY / hh:mm A");
    }

    dateTemplate(item) {
        return item.directBilling.validUntil ? DateUtil.formatDateMMM(item.directBilling.validUntil) : "";
    }

    sessionsTemplate(item) {
        const cptItems = item.directBilling.items || [];
        const maxApprovedSessions = cptItems.length > 0
            ? Math.max(...cptItems.map(cptItem => cptItem.noOfApprovedSessions || 0))
            : item.directBilling.noOfApprovedSessions;
        const header = `${item.directBilling.usedAppointmentIds.indexOf(item.appointmentId) + 1}/${maxApprovedSessions}`;

        if (cptItems.length === 0) {
            return <div style={{textAlign: 'center'}}>{header}</div>;
        }

        const perCptSessions = (
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px'}}>
                <tbody>
                    {cptItems.map((cptItem, index) => (
                        <tr key={index} style={{borderBottom: index < cptItems.length - 1 ? '1px solid #eee' : 'none'}}>
                            <td style={{padding: '2px 6px', fontWeight: 600}}>{cptItem.cpt?.cptCode || '-'}</td>
                            <td style={{padding: '2px 6px', textAlign: 'center'}}>
                                {cptItem.noOfUsedSessions ?? 0}/{cptItem.noOfApprovedSessions ?? 0}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );

        return (
            <div style={{textAlign: 'center'}}>
                <HoverableDescriptionBox
                    header={header}
                    description={perCptSessions}
                />
            </div>
        );
    }

    updateItem = (item, updatedField, newValue, callback = noop) => {
        const updateDto = pick(item, ['eligibilityStatus', 'patientInformed']);

        updateDto[updatedField] = newValue;

        RemotingService.putRemoteCall(
            `api/insurance/coordinator/v2/insurance-approval/${item.id}`,
            updateDto,
            () => {
                item[updatedField] = newValue && Array.isArray(newValue) ? [...newValue] : newValue;
                callback();
                this.forceUpdate();
            }, null, true, false);
    }

    modalityTemplate(item) {
        return EnumDirectBillingType[item.directBilling.modality]?.name || '';
    }

    listInputTemplate = (item, field, options) => {
        if (!this.canEditApprovals) {
            const selected = options.find(o => o.key === item[field]);
            return <span>{selected ? selected.name : ''}</span>;
        }
        return (
            <PrimeDropdown optionLabel="name" optionValue="key"
                           options={options} filter={true}
                           value={item[field]}
                           appendTo={"body", document.body}
                           onChange={event => this.updateItem(item, field, event.value)}
                           style={{width: '100%'}}
            />
        )
    }

    patientTemplate(item) {
        return  item.directBilling.patient.middleName ?
            item.directBilling.patient.firstName + " " + item.directBilling.patient.middleName + " " + item.directBilling.patient.lastName :
            item.directBilling.patient.firstName + " " + item.directBilling.patient.lastName;
    }

    render() {
        return (
            <>
                <div style={{padding: '10px'}}
                     className="data-table-header-wrapper"> {/*Remove this class and date navigation won't work.}*/}
                    <div className="float-left">
                        <PageTitle title="Appointments" className="bg-white"/>
                    </div>
                    <div className="float-right">
                        <DateNavigation onDateChange={this.handleDateChange} displayDate={true}/>
                    </div>
                </div>
                <div style={{padding: '10px'}}>
                    <DataTable className="InsuranceTable" responsive
                               emptyMessage={"No items found"}
                               value={this.state.items}
                               rows={20}
                               scrollable
                               paginator={true}
                               paginatorPosition="bottom">
                        <Column header="Visit Date/Time"  field="visitTime" sortable body={this.dateTimeTemplate}/>
                        <Column header="Provider" field="provider" filter={true} filterMatchMode="contains" sortable/>
                        <Column header="Modality" body={this.modalityTemplate}/>
                        <Column header="Sessions" body={this.sessionsTemplate}/>
                        <Column header="Patient" body={this.patientTemplate} filter={true} filterMatchMode="contains"
                                sortable/>
                        <Column header="MRN" field="directBilling.patient.mrn.mrnNo" filter={true} filterMatchMode="contains" sortable/>
                        <Column header="Insurance" field="directBilling.patientInsuranceInfo" filter={true} filterMatchMode="contains" sortable/>
                        <Column header="Ref No." field="directBilling.referenceNo" filter={true} filterMatchMode="contains" sortable/>
                        <Column header="Referred By" field="directBilling.referrerName"/>
                        <Column header="Valid Until" field="directBilling.validUntil" sortable body={(item) => DateUtil.formatDateMMM(item.directBilling.validUntil)}/>
                        <Column header="Status" field="eligibilityStatus"
                                body={(item) => this.listInputTemplate(item, 'eligibilityStatus', EligibilityStatusEnum.toArray())}/>
                        <Column header="Patient Informed" field="patientInformed"
                                body={(item) => this.listInputTemplate(item, 'patientInformed', YesNoNotAvailableEnum.toArray())}/>
                        <Column header="Note" body={this.notesTemplate}/>
                    </DataTable>
                    <InsuranceDocumentLegend/>
                </div>
            </>
        )
    }
}
